import { useMemo } from 'react';
import {
  defaultThemes,
  ThemeParameters,
  deriveNovaTokens,
  ThemeParameterOverrides,
} from '../../../design-tokens';
import { DesignTokens, useDesignTokens, useThemeContext } from '../../../theme';

/**
 * Hook that generates table override themes.
 *
 * @param ignoreTableOverrideTheme whether or not to use the base theme without table overrides applied
 * @param header whether or not to calculate the theme using header overrides or not
 */
export function useTableOverrideTheme(
  ignoreTableOverrideTheme = false,
  isHeader = false
): {
  currentTheme: ThemeParameters;
  inverseTheme: ThemeParameters;
} {
  const dt = useDesignTokens();
  const themeContext = useThemeContext?.();
  const overrideTheme = useMemo(() => {
    // Fallback to the light theme if theme context is undefined
    if (!themeContext) {
      return { currentTheme: defaultThemes.light, inverseTheme: defaultThemes.light };
    }
    if (
      (isHeader && !dt.overrides.table.headerLabelColor) ||
      (!isHeader && !dt.overrides.table.textColor)
    ) {
      return { currentTheme: themeContext.currentTheme, inverseTheme: themeContext.inverseTheme };
    }
    const new_dt = deriveNovaTokens(
      getTableOverrideTheme(
        dt,
        isHeader ? dt.overrides.table.headerLabelColor : dt.overrides.table.textColor
      )
    );
    return {
      currentTheme: {
        ...themeContext.currentTheme,
        colors: {
          ...themeContext.currentTheme.colors,
          ...new_dt,
          colorTextDefault:
            (isHeader ? dt.overrides.table.headerLabelColor : dt.overrides.table.textColor) ??
            new_dt.colorTextDefault,
        },
      },
      inverseTheme: themeContext.inverseTheme,
    };
  }, [themeContext, isHeader, dt]);
  return ignoreTableOverrideTheme ? themeContext : overrideTheme;
}

/**
 * Overrides high level color variables that Table needs.
 *
 * @param dt design token object
 */
const getTableOverrideTheme = (dt: DesignTokens, fg?: string): ThemeParameterOverrides => {
  return {
    baseThemeName: dt.themeVariant,
    colors: {
      ...dt.c,
      colorNeutral: !!fg ? fg : dt.c.colorNeutral,
      colorStatusNeutral: !!fg ? fg : dt.c.colorNeutral,
    },
    overrides: {},
  };
};
